import { useEffect, useRef, useState } from "react";
export type DestinationKind = "articles" | "places" | "events";
export type DestinationResult = { id: string; title: string; subtitle: string };
type Props = {
  kind: DestinationKind;
  selected: DestinationResult | null;
  onSelect: (value: DestinationResult | null) => void;
  search: (
    kind: DestinationKind,
    query: string,
  ) => Promise<DestinationResult[]>;
};
export function DestinationSearch({ kind, selected, onSelect, search }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DestinationResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setQuery("");
    setResults([]);
    setSearched(false);
    setBusy(false);
    setError("");
    return () => {
      generation.current++;
    };
  }, [kind]);
  async function submit() {
    if (query.trim().length < 2) return;
    const current = ++generation.current;
    setBusy(true);
    setError("");
    setSearched(false);
    setResults([]);
    try {
      const found = await search(kind, query.trim());
      if (current === generation.current) {
        setResults(found);
        setSearched(true);
      }
    } catch (e) {
      if (current === generation.current) {
        setError(e instanceof Error ? e.message : "Recherche indisponible.");
      }
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  return (
    <div className="space-y-3">
      <label
        className="block text-sm"
        htmlFor="notification-destination-search"
      >
        {kind === "articles"
          ? "Rechercher par titre ou sous-titre"
          : kind === "places"
          ? "Rechercher par nom ou ville"
          : "Rechercher par titre ou description courte"}
      </label>
      <div className="flex gap-2">
        <input
          id="notification-destination-search"
          value={query}
          maxLength={100}
          className="min-w-0 flex-1 rounded-xl border border-black/15 px-3 py-2 text-sm"
          placeholder="Deux caractères minimum"
          onChange={(e) => {
            generation.current++;
            setBusy(false);
            setQuery(e.target.value);
            setResults([]);
            setSearched(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <button
          type="button"
          disabled={busy || query.trim().length < 2}
          className="rounded-xl border border-black/15 px-3 py-2 text-sm disabled:opacity-40"
          onClick={() => void submit()}
        >
          {busy ? "Recherche…" : "Rechercher"}
        </button>
      </div>
      {selected && (
        <div role="status" className="rounded-xl bg-yellow-50 p-3 text-sm">
          <span className="font-semibold">Sélection : {selected.title}</span>
          <button
            type="button"
            className="ml-3 underline"
            onClick={() =>
              onSelect(null)}
          >
            Retirer
          </button>
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {searched && (
        <div aria-live="polite">
          <ul
            aria-label="Résultats de recherche"
            className="max-h-64 overflow-y-auto rounded-xl border border-black/10"
          >
            {results.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 border-b border-black/5 p-3 last:border-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{item.title}</p>
                  {item.subtitle && (
                    <p className="line-clamp-2 text-xs text-neutral-500">
                      {item.subtitle}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={selected?.id === item.id}
                  className="shrink-0 rounded-lg bg-yellow-200 px-3 py-2 text-sm disabled:bg-neutral-100"
                  onClick={() => {
                    onSelect(item);
                    setResults([]);
                    setSearched(false);
                  }}
                >
                  {selected?.id === item.id ? "Ajouté" : "Ajouter"}
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="p-3 text-sm text-neutral-500">
                Aucun contenu publié trouvé.
              </li>
            )}
          </ul>
          {results.length === 20 && (
            <p className="mt-2 text-xs text-neutral-500">
              20 résultats affichés. Précisez votre recherche si nécessaire.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
